// Map layers (the full Map, playermap.js): what the map shows at which zoom,
// from data the client already holds (main.js module globals → the `src`
// getters PlayerMap gets). Pure-ish painters: each takes a 2D context in the
// map's WORLD transform (metres) and `k` = metres per CSS px, culls to the
// view's world AABB `rect` and never allocates per element.
//
//   LAYERS        the zoom table (max CSS m/px a layer shows at; monotonic:
//                 zooming in never removes a layer — check-view.mjs)
//   LABEL_RULES   per-kind label minimum zoom + priority + style
//   labels        collectLabels (gazetteer pois, stations, streets along the
//                 longest straight visible run, building names, house
//                 numbers, guidepost names) → placeLabels: greedy, priority
//                 order, against a screen occupancy grid — deterministic, no
//                 overlaps — drawn in SCREEN space (upright on a rotated map)
//   transforms    toScreen / toWorld (rotation-aware; _paint's toS + inverse)
//   misc          niceScale (1-2-5 scale bar), hoverPick (the desktop
//                 tooltip: gazetteer + the registry grid, NOT resolveHits),
//                 registryIndex / recordsNear (shared with inspect.js flat2d)
// No DOM, no three: node-importable (scripts/check-view.mjs).
import { LABEL_ICON } from './scopehud.js';
import { t } from './i18n.js';
import './i18n/player.js';

// ------------------------------------------------------------ zoom tables
// [layer, max CSS m/px] coarse → fine. Infinity = any zoom. The trail ladder
// (MAP-D, owner: "when zooming the 2d map more and more should be shown"):
//   ≤16  network   official (swissTLM3D) trails + TLM-only segments by mark
//        routes    nwn/rwn/ncn/rcn (+ international) route bands under them
//   ≤8   trails    every path (graded / ungraded) — TRAILS-C
//   ≤4   shields   route number plates repeated along the stages
//        mtb       MTB-scale colour casing (MTB chip on)
//   ≤2   chips     grade chips T3 / K3 / S2 on the lines
//        names     route + trail names along the lines
//        landmarks saddles, summit crosses (amenities)
//   ≤1   trailstyle width (oa) + surface (bel) + poor-visibility dots
//        chevrons  incline arrows (tt.incline)
//        amenities water, toilets, fire pits, picnic tables, shelters, bus stops
//                  (DETAIL-1 point barriers: never on the map — STREET_OFFMAP)
//        (signposts: the guidepost labels gain the elevation)
//   ≤0.5 minor     benches, bins, boards, maps, milestones, art, chargers, parking
//        structs   swissTLM3D bridges / steps / fords / underpasses on paths
export const LAYERS = [
  ['lakes', Infinity], ['rivers', Infinity], ['lifts', Infinity], ['stations', Infinity],
  ['network', 16], ['routes', 16],
  ['trails', 8], ['roads', 8],
  ['shields', 4], ['mtb', 4],
  ['buildings', 2], ['footprints', 2], ['chips', 2], ['names', 2], ['landmarks', 2],
  ['guideposts', 1], ['objects', 1], ['trailstyle', 1], ['chevrons', 1], ['amenities', 1],
  ['trees', 0.5], ['shrubs', 0.5], ['minor', 0.5], ['structs', 0.5],
];
const LAYER_MAX = Object.fromEntries(LAYERS);
export const layerOn = (name, mppCss) => mppCss <= (LAYER_MAX[name] ?? Infinity);
export const layersAt = (mppCss) => LAYERS.filter(([, m]) => mppCss <= m).map(([n]) => n);

// kind: [max CSS m/px, priority, size CSS px, colour, style]
// style: place (centred, bold) · water (centred, italic blue) · peak (▲ +
// text right) · dot (symbol + text right) · line (along a street / trail /
// route) · num (centred, small: house numbers) · shield (a route number
// plate, centred) · chip (grade chip(s) in the mark colour, centred) · icon
// (an amenity glyph + optional text right).
// Trail ladder priorities (MAP-D): shields > grade chips > names > amenities.
export const LABEL_RULES = {
  shield: [4, 48, 10, '#ffffff', 'shield'],
  chip: [2, 42, 9.5, '#111111', 'chip'],
  saddle: [2, 37, 11, '#e5e7eb', 'icon'],
  cross: [2, 31, 10.5, '#f5f5f4', 'icon'],
  routename: [2, 27, 11, '#d9f99d', 'line'],
  trailname: [2, 24, 10.5, '#fef3c7', 'line'],
  amenity: [1, 14, 10, '#e5e7eb', 'icon'],
  amenity_minor: [0.5, 12, 9.5, '#d1d5db', 'icon'],
  city: [Infinity, 100, 15, '#ffffff', 'place'],
  town: [Infinity, 98, 15, '#ffffff', 'place'],
  village: [Infinity, 95, 14, '#ffffff', 'place'],
  suburb: [Infinity, 80, 13, '#f1f5fb', 'place'],
  hamlet: [Infinity, 70, 12, '#f1f5fb', 'place'],
  lake: [Infinity, 85, 12.5, '#bfe3ff', 'water'],
  peak: [Infinity, 60, 12, '#ffe9d0', 'peak'],          // + prominence
  farpeak: [Infinity, 36, 11, '#d9cbb8', 'peak'],
  station: [6, 55, 11.5, '#ffe7a8', 'dot'],
  alpine_hut: [6, 52, 11.5, '#f5d0fe', 'dot'],
  hut: [6, 50, 11.5, '#f5d0fe', 'dot'],
  pass: [6, 50, 11.5, '#e5e7eb', 'dot'],
  river: [6, 45, 11.5, '#bfe3ff', 'water'],
  ridge: [6, 40, 11, '#e5e7eb', 'dot'],
  viewpoint: [6, 40, 11, '#bbf7d0', 'dot'],
  church: [2, 34, 11, '#e0e7ff', 'dot'],
  chapel: [2, 32, 11, '#e0e7ff', 'dot'],
  hospital: [2, 34, 11, '#fecaca', 'dot'],
  hotel: [2, 32, 11, '#f5d0fe', 'dot'],
  hostel: [2, 30, 11, '#f5d0fe', 'dot'],
  museum: [2, 32, 11, '#e0e7ff', 'dot'],
  gallery: [2, 30, 11, '#e0e7ff', 'dot'],
  attraction: [2, 30, 11, '#e0e7ff', 'dot'],
  restaurant: [2, 30, 11, '#fed7aa', 'dot'],
  cafe: [2, 28, 11, '#fed7aa', 'dot'],
  bar: [2, 28, 11, '#fed7aa', 'dot'],
  poi: [2, 26, 11, '#e5e7eb', 'dot'],                 // any other gazetteer kind
  street: [2, 25, 11, '#fff7e0', 'line'],
  building: [2, 20, 10.5, '#fde68a', 'dot'],
  guidepost: [1, 22, 10.5, '#fde047', 'dot'],
  address: [0.5, 10, 9.5, '#e5e7eb', 'num'],
};
export const labelRule = (kind) => LABEL_RULES[kind] || LABEL_RULES.poi;

// symbol colours of the dot-style kinds (food / lodging / culture / nature)
const SYM = {
  restaurant: '#fb923c', cafe: '#fb923c', bar: '#fb923c',
  hotel: '#c084fc', hostel: '#c084fc', alpine_hut: '#c084fc', hut: '#c084fc',
  church: '#a5b4fc', chapel: '#a5b4fc', museum: '#a5b4fc', gallery: '#a5b4fc', attraction: '#a5b4fc',
  viewpoint: '#4ade80', hospital: '#f87171', station: '#ffc93c', guidepost: '#F2C400',
  building: '#fde68a',
};

// ------------------------------------------------------------ transforms
// view {cx, cz, mpp (m per canvas px), rot (map-up bearing, rad)}
export function toScreen(v, W, H, x, z) {
  const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
  const dx = (x - v.cx) / v.mpp, dz = (z - v.cz) / v.mpp;
  return [W / 2 + dx * c + dz * s, H / 2 - dx * s + dz * c];
}
export function toWorld(v, W, H, sx, sy) {
  const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
  const u = sx - W / 2, w = sy - H / 2;
  return [v.cx + (u * c - w * s) * v.mpp, v.cz + (u * s + w * c) * v.mpp];
}
// the canvas world transform of a view (what _paint applies)
export function applyWorld(g, v, W, H) {
  g.translate(W / 2, H / 2); g.rotate(-(v.rot || 0)); g.scale(1 / v.mpp, 1 / v.mpp); g.translate(-v.cx, -v.cz);
}

// 1-2-5 scale bar: the longest nice length ≤ maxPx CSS px → {m, px, text}
export function niceScale(mppCss, maxPx = 120) {
  const m = mppCss * maxPx;
  if (!(m > 0)) return null;
  const p = 10 ** Math.floor(Math.log10(m));
  const L = [5, 2, 1].map((f) => f * p).find((x) => x <= m) || p;
  return { m: L, px: L / mppCss, text: L >= 1000 ? `${+(L / 1000).toFixed(3)} km` : `${+L.toFixed(3)} m` };
}

const hit = (b, r) => !(b[2] < r[0] || b[0] > r[2] || b[3] < r[1] || b[1] > r[3]);

// ------------------------------------------------------- cached indices
const _bb = new WeakMap();
// bbox of a [[x,y,z]…] / [[x,z]…] polyline (zi = the z index)
function bboxOf(o, pts, zi) {
  let b = _bb.get(o);
  if (b) return b;
  b = [Infinity, Infinity, -Infinity, -Infinity];
  for (const p of pts) {
    if (p[0] < b[0]) b[0] = p[0]; if (p[0] > b[2]) b[2] = p[0];
    if (p[zi] < b[1]) b[1] = p[zi]; if (p[zi] > b[3]) b[3] = p[zi];
  }
  _bb.set(o, b);
  return b;
}

// building registry ({buildings: {uuid: {x, z, name, gwr:{addr}}}}) → a 64 m
// grid of {x, z, name, addr, hn (house number), uuid, rec}; cached per object
const REG_CELL = 64;
const _reg = new WeakMap();
export function registryIndex(reg) {
  if (!reg || !reg.buildings) return null;
  let ix = _reg.get(reg);
  if (ix) return ix;
  const grid = new Map(), list = [];
  for (const uuid in reg.buildings) {
    const r = reg.buildings[uuid];
    if (!r || typeof r.x !== 'number' || typeof r.z !== 'number') continue;
    const g = r.gwr || {}, o = r.osm || {};
    const addr = g.addr || (o['addr:street'] ? `${o['addr:street']}${o['addr:housenumber'] ? ' ' + o['addr:housenumber'] : ''}` : '');
    const m = /^(.*\D)\s+(\d+\s?[a-zA-Z]?)$/.exec(addr || '');
    let name = r.name || o.name || '';
    if (name && addr && name === addr) name = '';
    const e = { x: r.x, z: r.z, name, addr, hn: m ? m[2].replace(/\s/g, '') : addr, uuid, rec: r };
    list.push(e);
    const k = Math.floor(r.x / REG_CELL) * 65536 + Math.floor(r.z / REG_CELL);
    let a = grid.get(k);
    if (!a) grid.set(k, a = []);
    a.push(e);
  }
  ix = { grid, list };
  _reg.set(reg, ix);
  return ix;
}
// every indexed record in rect [x0, z0, x1, z1] → fn(e)
export function recordsIn(ix, rect, fn) {
  if (!ix) return;
  const cx0 = Math.floor(rect[0] / REG_CELL), cx1 = Math.floor(rect[2] / REG_CELL);
  const cz0 = Math.floor(rect[1] / REG_CELL), cz1 = Math.floor(rect[3] / REG_CELL);
  if ((cx1 - cx0 + 1) * (cz1 - cz0 + 1) > ix.list.length) {
    for (const e of ix.list) if (e.x >= rect[0] && e.x <= rect[2] && e.z >= rect[1] && e.z <= rect[3]) fn(e);
    return;
  }
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cz = cz0; cz <= cz1; cz++) {
      const a = ix.grid.get(cx * 65536 + cz);
      if (!a) continue;
      for (const e of a) if (e.x >= rect[0] && e.x <= rect[2] && e.z >= rect[1] && e.z <= rect[3]) fn(e);
    }
  }
}
// the nearest registry record within r of (x, z) → {e, d} | null
export function recordsNear(ix, x, z, r) {
  let best = null, bd = r;
  recordsIn(ix, [x - r, z - r, x + r, z + r], (e) => {
    const d = Math.hypot(e.x - x, e.z - z);
    if (d <= bd) { bd = d; best = e; }
  });
  return best ? { e: best, d: bd } : null;
}

// ------------------------------------------------------- world painters
// polyline [[x,(y,)z]…] into the current path, skipping vertices closer than
// minD (m) to the last one drawn (a far view does not trace every 4 m vertex)
function trace(g, pts, zi, minD) {
  let lx = pts[0][0], lz = pts[0][zi];
  g.moveTo(lx, lz);
  const m2 = minD * minD, n = pts.length - 1;
  for (let i = 1; i <= n; i++) {
    const p = pts[i], dx = p[0] - lx, dz = p[zi] - lz;
    if (i < n && dx * dx + dz * dz < m2) continue;
    g.lineTo(p[0], p[zi]); lx = p[0]; lz = p[zi];
  }
}

// lakes: {poly, rings:[outer, ...holes]} — fill (even-odd) + shore line
export function paintLakes(g, lakes, rect, k) {
  if (!lakes || !lakes.length) return;
  g.beginPath();
  let any = false;
  for (const l of lakes) {
    const poly = l.poly || l.polygon;
    if (!poly || poly.length < 3 || !hit(bboxOf(l, poly, 1), rect)) continue;
    for (const ring of l.rings || [poly]) {
      if (!ring || ring.length < 3) continue;
      trace(g, ring, 1, k * 0.8);
      g.closePath();
    }
    any = true;
  }
  if (!any) return;
  g.fillStyle = 'rgba(62,132,206,0.42)'; g.fill('evenodd');
  g.strokeStyle = 'rgba(150,205,255,0.85)'; g.lineWidth = 1.1 * k; g.setLineDash([]); g.stroke();
}

// rivers: {w, pts:[[x,y,z]]} — width w (m), never under 1.4 CSS px
export function paintRivers(g, ways, rect, k) {
  if (!ways || !ways.length) return;
  g.strokeStyle = 'rgba(96,165,240,0.9)'; g.lineCap = 'round'; g.lineJoin = 'round'; g.setLineDash([]);
  for (const w of ways) {
    if (!w.pts || w.pts.length < 2 || !hit(bboxOf(w, w.pts, 2), rect)) continue;
    g.beginPath(); trace(g, w.pts, 2, k * 1.2);
    g.lineWidth = Math.max(w.w || 2, 1.4 * k); g.stroke();
  }
  g.lineCap = 'butt';
}

// roads (cased cream) + rails (dashed) of the way graph; paths are the trail layer
export function paintRoads(g, graph, rect, k) {
  const ways = graph && graph.ways;
  if (!ways || !ways.length) return;
  const minD = k * 1.2;
  const pass = (kind, style, width, dash) => {
    g.beginPath();
    let any = false;
    for (const w of ways) {
      if (w.kind !== kind || w.pts.length < 2 || !hit(bboxOf(w, w.pts, 2), rect)) continue;
      trace(g, w.pts, 2, minD); any = true;
    }
    if (!any) return;
    g.strokeStyle = style; g.lineWidth = width; g.setLineDash(dash || []); g.stroke();
  };
  g.lineCap = 'round'; g.lineJoin = 'round';
  const fine = k <= 1;     // zoomed in: the imagery shows the road, the line only marks it
  pass('road', fine ? 'rgba(40,32,24,0.45)' : 'rgba(40,32,24,0.7)', 3.4 * k);
  pass('road', fine ? 'rgba(255,247,224,0.6)' : 'rgba(255,247,224,0.92)', 2 * k);
  g.lineCap = 'butt';
  pass('rail', 'rgba(30,30,36,0.85)', 2.4 * k);
  pass('rail', 'rgba(235,235,240,0.95)', 1.2 * k, [5 * k, 5 * k]);
  g.setLineDash([]);
}

// resident collision footprints (near the player only): {fp:[[[x,z]…]], bbox, uuid}
// → the uuids drawn (their registry dots are skipped)
export function paintFootprints(g, collider, rect, k) {
  const drawn = new Set();
  if (!collider || !collider.tiles) return drawn;
  g.beginPath();
  let any = false;
  for (const items of collider.tiles.values()) {
    for (const b of items) {
      if (!b.fp || !b.fp.length || !b.bbox || !hit(b.bbox, rect)) continue;
      for (const ring of b.fp) { if (ring.length >= 3) { trace(g, ring, 1, k * 0.5); g.closePath(); } }
      if (b.uuid) drawn.add(b.uuid);
      any = true;
    }
  }
  if (any) {
    g.fillStyle = 'rgba(244,232,214,0.62)'; g.fill('evenodd');
    g.strokeStyle = 'rgba(70,52,36,0.85)'; g.lineWidth = 1 * k; g.setLineDash([]); g.stroke();
  }
  return drawn;
}

// registry building dots (everywhere; skip the ones a footprint already shows)
export function paintBuildingDots(g, ix, rect, k, skip = null) {
  if (!ix) return;
  const r = 2.2 * k;
  g.beginPath();
  recordsIn(ix, rect, (e) => {
    if (skip && skip.has(e.uuid)) return;
    g.rect(e.x - r, e.z - r, 2 * r, 2 * r);
  });
  g.fillStyle = 'rgba(253,230,138,0.92)'; g.fill();
  g.strokeStyle = 'rgba(40,30,20,0.9)'; g.lineWidth = 0.8 * k; g.stroke();
}

// objects.json runs, coloured by archetype group
const OBJ_GROUP = {
  avalanche: 'barrier', checkdam: 'barrier', wall: 'barrier', dam: 'barrier',
  mast: 'tech', tower: 'tech', chimney: 'tech', windturbine: 'tech', powerline: 'tech', utility: 'tech', silo: 'tech',
  court: 'sport', lane: 'sport', skijump: 'sport', chute: 'sport',
  fountain: 'water', basin: 'water', waterfall: 'water', jetty: 'water',
};
const OBJ_STYLE = {
  barrier: ['rgba(214,170,120,0.95)', 1.8], tech: ['rgba(196,181,253,0.95)', 1.6],
  sport: ['rgba(251,146,60,0.95)', 2], water: ['rgba(103,232,249,0.95)', 2], other: ['rgba(220,220,220,0.9)', 1.6],
};
export function paintObjects(g, data, rect, k) {
  const objs = data && data.objects;
  if (!objs || !objs.length) return;
  g.lineCap = 'round'; g.lineJoin = 'round'; g.setLineDash([]);
  for (const grp of Object.keys(OBJ_STYLE)) {
    g.beginPath();
    let any = false;
    for (const o of objs) {
      if ((OBJ_GROUP[o.a] || 'other') !== grp || !o.runs) continue;
      let b = _bb.get(o);
      if (!b) {
        b = [Infinity, Infinity, -Infinity, -Infinity];
        for (const run of o.runs) for (const p of run) {
          if (p[0] < b[0]) b[0] = p[0]; if (p[0] > b[2]) b[2] = p[0];
          if (p[2] < b[1]) b[1] = p[2]; if (p[2] > b[3]) b[3] = p[2];
        }
        _bb.set(o, b);
      }
      if (!hit(b, rect)) continue;
      for (const run of o.runs) {
        if (!run || !run.length) continue;
        if (run.length === 1) { g.moveTo(run[0][0] - k, run[0][2]); g.lineTo(run[0][0] + k, run[0][2]); continue; }
        trace(g, run, 2, k * 0.6);
      }
      any = true;
    }
    if (!any) continue;
    const [col, w] = OBJ_STYLE[grp];
    g.strokeStyle = col; g.lineWidth = w * k; g.stroke();
  }
  g.lineCap = 'butt';
}

// guideposts {x, z}: the Swiss yellow signpost diamond
export function paintGuideposts(g, posts, rect, k) {
  const list = posts && posts.posts;
  if (!list || !list.length) return;
  const s = 4.2 * k;
  g.beginPath();
  for (const p of list) {
    if (p.x < rect[0] || p.x > rect[2] || p.z < rect[1] || p.z > rect[3]) continue;
    g.moveTo(p.x, p.z - s); g.lineTo(p.x + s, p.z); g.lineTo(p.x, p.z + s); g.lineTo(p.x - s, p.z); g.closePath();
  }
  g.fillStyle = '#F2C400'; g.fill();
  g.strokeStyle = 'rgba(20,20,20,0.9)'; g.lineWidth = 1 * k; g.stroke();
}

// trees: crown circles at the MEASURED radius (the pack's 0.2 m units; unmeasured →
// from the height), culled by forest.byChunk (chunkM)
export function paintTrees(g, forest, rect, k) {
  if (!forest || !forest.xyz || !forest.byChunk) return;
  if (forest.wantRect) forest.wantRect(rect);   // the view's tree tiles (trees.js VegTiles), bulk
  const C = forest.chunkM || 1024, xyz = forest.xyz, rad = forest.radius, sc = forest.scales;
  const meas = !!forest.measured;
  const minR = 0.6 * k;
  g.beginPath();
  for (let cx = Math.floor(rect[0] / C); cx <= Math.floor(rect[2] / C); cx++) {
    for (let cz = Math.floor(rect[1] / C); cz <= Math.floor(rect[3] / C); cz++) {
      const idx = forest.byChunk.get(cx + '_' + cz);
      if (!idx) continue;
      for (const i of idx) {
        const x = xyz[i * 3], z = xyz[i * 3 + 2];
        if (x < rect[0] || x > rect[2] || z < rect[1] || z > rect[3]) continue;
        let r = rad && rad[i] ? rad[i] * 0.2 : (sc ? (sc[i] / (meas ? 8 : 10)) * 0.22 : 2);
        if (r < minR) r = minR;
        g.moveTo(x + r, z); g.arc(x, z, r, 0, Math.PI * 2);
      }
    }
  }
  g.fillStyle = 'rgba(20,74,36,0.5)'; g.fill();
  g.strokeStyle = 'rgba(10,40,18,0.55)'; g.lineWidth = 0.7 * k; g.stroke();
}

// shrubs: small dots (height m×50 → a crown of ~0.45 h), byCell (cellM)
export function paintShrubs(g, shrubs, rect, k) {
  if (!shrubs || !shrubs.xyz || !shrubs.byCell) return;
  if (shrubs.wantRect) shrubs.wantRect(rect);   // the view's shrub tiles, bulk
  const C = shrubs.cellM || 128, xyz = shrubs.xyz, hg = shrubs.hgt;
  const minR = 0.45 * k;
  g.beginPath();
  for (let cx = Math.floor(rect[0] / C); cx <= Math.floor(rect[2] / C); cx++) {
    for (let cz = Math.floor(rect[1] / C); cz <= Math.floor(rect[3] / C); cz++) {
      const idx = shrubs.byCell.get(cx + '_' + cz);
      if (!idx) continue;
      for (const i of idx) {
        const x = xyz[i * 3], z = xyz[i * 3 + 2];
        if (x < rect[0] || x > rect[2] || z < rect[1] || z > rect[3]) continue;
        let r = hg ? Math.min(3, (hg[i] / 50) * 0.45) : 0.8;
        if (r < minR) r = minR;
        g.moveTo(x + r, z); g.arc(x, z, r, 0, Math.PI * 2);
      }
    }
  }
  g.fillStyle = 'rgba(74,128,52,0.6)'; g.fill();
}

// ------------------------------------------------- trails, routes, POIs
// (MAP-D) Colours = the contract sRGB of trailmarks.js MARK_COLOUR /
// MTB_COLOUR (duplicated: this module stays three-free; check-trails.mjs
// asserts they agree).
export const MARK_RGB = { yellow: '#F2C400', red: '#D52B1E', blue: '#1F5FBF', white: '#F4F4F4' };
export const MTB_RGB = { s0: '#2E9E44', s1: '#1F5FBF', s2: '#D52B1E', s3: '#151515' };
// mark → [colour, width CSS px, dash CSS px] — the TRAILS-C map convention
// (Wanderweg yellow solid, Bergwanderweg red dashed, Alpinwanderweg blue
// dashed, via ferrata blue dotted, ungraded thin grey). The mini / wrist map
// paints exactly these; the full Map refines them ≤ 1 m/px (trailLook).
export const MARK_LINE = {
  none: ['rgba(228,228,228,.5)', 0.9, null],
  yellow: [MARK_RGB.yellow, 2, null],
  wrw: [MARK_RGB.red, 2.2, [6, 4]],
  wbw: [MARK_RGB.blue, 2.2, [6, 4]],
  ferrata: [MARK_RGB.blue, 2.6, [0.5, 3.5]],
};
// grade chip [background, text] per mark (the chip is the line's colour)
export const CHIP_STYLE = { yellow: ['#F2C400', '#111111'], wrw: ['#D52B1E', '#ffffff'], wbw: ['#1F5FBF', '#ffffff'],
  ferrata: ['#1F5FBF', '#ffffff'], none: ['#e5e7eb', '#111111'] };
// swissTLM3D object class (pathinfo oa) → line width CSS px (≤ 1 m/px)
export const OA_WIDTH = { path1: 1.6, path2: 2.2, road3: 2.8, road4: 3.2, road6: 3.8, road8: 4.2, road10: 4.6,
  spur: 1.5, ferrata: 2.6, frag: 1.3 };
const POOR_VIS = new Set(['bad', 'horrible', 'no']);

// the full Map's look of one trail entry {mark, oa, bel, vis, extra} —
// coarse (> 1 m/px) = MARK_LINE; fine (≤ 1 m/px): width by the official
// class, a hard surface gets a pale casing, a natural one a finer line, poor
// visibility dotted, a marked route without a built path (spur) sparse
// dashes, a fragment faint; TLM-only segments (extra, R5) at 70 %. `key`
// batches the strokes (one per key).
export function trailLook(e, fine) {
  const [col, w0, d0] = MARK_LINE[e.mark] || MARK_LINE.none;
  let w = w0, dash = d0, alpha = e.extra ? 0.7 : 1, cas = 0;
  if (fine) {
    if (e.oa && OA_WIDTH[e.oa]) w = OA_WIDTH[e.oa] * (e.mark === 'none' ? 0.6 : 1);
    if (e.bel === 'natural') w *= 0.8;
    else if (e.bel === 'hard') cas = w + 1.8;
    if (e.oa === 'spur') dash = [1.2, 4];
    if (POOR_VIS.has(e.vis)) dash = [0.5, 2.6];
    if (e.oa === 'frag') alpha *= 0.6;
  }
  const rank = e.mark === 'none' ? 0 : e.extra ? 1 : 2;
  return { col, w, dash, alpha, cas, rank,
    key: `${rank}|${col}|${w.toFixed(2)}|${dash ? dash.join(',') : ''}|${alpha.toFixed(2)}|${cas.toFixed(2)}` };
}

// entries → [{look, items}] batched by look key, drawn ungraded → TLM-only →
// graded (filter: which entries take part). Built once per data generation.
// trailBucketsSteps = THE batching, a step per `every` entries (R8 M1);
// trailBuckets = its drain.
export function trailBuckets(entries, fine, filter = null) {
  const it = trailBucketsSteps(entries, fine, filter);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* trailBucketsSteps(entries, fine, filter = null, every = 256) {
  const by = new Map();
  let k = 0;
  for (const e of entries) {
    if (++k % every === 0) yield;
    if (!e.pts || e.pts.length < 4 || (filter && !filter(e))) continue;
    const look = trailLook(e, fine);
    let b = by.get(look.key);
    if (!b) by.set(look.key, b = { look, items: [] });
    b.items.push(e);
  }
  return [...by.values()].sort((a, b) => a.look.rank - b.look.rank || (a.look.key < b.look.key ? -1 : a.look.key > b.look.key ? 1 : 0));
}

// interleaved xz polyline into the current path (vertices closer than minD
// to the last drawn one are skipped)
function traceF(g, P, minD) {
  let lx = P[0], lz = P[1];
  g.moveTo(lx, lz);
  const m2 = minD * minD, n = P.length - 2;
  for (let i = 2; i <= n; i += 2) {
    const dx = P[i] - lx, dz = P[i + 1] - lz;
    if (i < n && dx * dx + dz * dz < m2) continue;
    g.lineTo(P[i], P[i + 1]); lx = P[i]; lz = P[i + 1];
  }
}

// the bucketed trail strokes (world transform; k = m per CSS px)
export function paintTrailBuckets(g, buckets, rect, k) {
  const minD = k * 0.8;
  for (const { look, items } of buckets) {
    g.beginPath();
    let any = false;
    for (const e of items) {
      if (!hit(e.bbox, rect)) continue;
      traceF(g, e.pts, minD); any = true;
    }
    if (!any) continue;
    g.globalAlpha = look.alpha;
    g.lineJoin = 'round';
    if (look.cas) {
      g.setLineDash([]); g.lineCap = 'round';
      g.strokeStyle = 'rgba(250,246,236,0.72)'; g.lineWidth = look.cas * k; g.stroke();
    }
    g.strokeStyle = look.col; g.lineWidth = look.w * k;
    g.lineCap = look.dash && look.dash[0] < 1 ? 'round' : 'butt';
    g.setLineDash(look.dash ? look.dash.map((d) => d * k) : []);
    g.stroke();
  }
  g.globalAlpha = 1; g.setLineDash([]); g.lineCap = 'butt';
}

// MTB colour casing (S0 green … S3+ black) under the lines of every entry
// with an MTB scale: [{key, items}] built once; drawn when the MTB chip is on
export function mtbBuckets(entries) {
  const by = new Map();
  for (const e of entries) {
    if (!e.mtb || !e.pts || e.pts.length < 4) continue;
    let b = by.get(e.mtb.key);
    if (!b) by.set(e.mtb.key, b = { key: e.mtb.key, items: [] });
    b.items.push(e);
  }
  return [...by.values()].sort((a, b) => (a.key < b.key ? -1 : 1));
}
export function paintMtb(g, buckets, rect, k) {
  g.setLineDash([]); g.lineCap = 'round'; g.lineJoin = 'round';
  for (const { key, items } of buckets) {
    g.beginPath();
    let any = false;
    for (const e of items) { if (hit(e.bbox, rect)) { traceF(g, e.pts, k * 0.8); any = true; } }
    if (!any) continue;
    g.strokeStyle = MTB_RGB[key]; g.globalAlpha = 0.85;
    g.lineWidth = 4.6 * k; g.stroke();
  }
  g.globalAlpha = 1; g.lineCap = 'butt';
}

// incline chevrons (≤ 1 m/px): entries with `inc` ±1 (up / down along the
// run, tt.incline) and a precomputed `arc` — every 34 CSS px, pointing uphill
export function paintChevrons(g, entries, rect, k) {
  const sp = 34 * k, s = 3.2 * k;
  g.beginPath();
  let any = false;
  for (const e of entries) {
    if (!e.inc || !e.arc || !hit(e.bbox, rect)) continue;
    const L = e.arc[e.arc.length - 1];
    for (let d = sp / 2; d < L; d += sp) {
      const [x, z, ux0, uz0] = arcPoint(e.pts, e.arc, d);
      if (x < rect[0] || x > rect[2] || z < rect[1] || z > rect[3]) continue;
      const ux = ux0 * e.inc, uz = uz0 * e.inc;
      g.moveTo(x - ux * s - uz * s, z - uz * s + ux * s);
      g.lineTo(x + ux * s * 0.6, z + uz * s * 0.6);
      g.lineTo(x - ux * s + uz * s, z - uz * s - ux * s);
      any = true;
    }
  }
  if (!any) return;
  g.setLineDash([]); g.lineCap = 'round'; g.lineJoin = 'round';
  g.strokeStyle = 'rgba(12,14,20,0.9)'; g.lineWidth = 2.6 * k; g.stroke();
  g.strokeStyle = '#ffffff'; g.lineWidth = 1.2 * k; g.stroke();
  g.lineCap = 'butt';
}

// routes (meta.routes, prepRoutes): route family → band / plate colours.
// SchweizMobil fields: Wanderland green, Veloland light blue, Mountainbike-
// land ochre; a local (lwn / lcn …) route: a white plate with the family's
// border + text. Bands only for the big networks (national / regional /
// international), soft and wide UNDER the trail lines.
export const ROUTE_FAMILY = { hiking: 'hike', foot: 'hike', mtb: 'mtb', bicycle: 'bike' };
export const ROUTE_STYLE = {
  hike: { band: 'rgba(56,168,72,0.30)', plate: ['#2F8F3F', '#ffffff', '#ffffff'], local: ['#ffffff', '#23702F', '#2F8F3F'], icon: '🥾' },
  bike: { band: 'rgba(62,155,216,0.30)', plate: ['#3E9BD8', '#ffffff', '#ffffff'], local: ['#ffffff', '#1F6FA8', '#3E9BD8'], icon: '🚲' },
  mtb: { band: 'rgba(217,138,31,0.32)', plate: ['#D98A1F', '#ffffff', '#ffffff'], local: ['#ffffff', '#9A5A0B', '#D98A1F'], icon: '🚵' },
};
export const BIG_NET = new Set(['iwn', 'nwn', 'rwn', 'icn', 'ncn', 'rcn']);
// (getters: the word is resolved in the current language at each read)
export const NETWORK_LABEL = {
  get iwn() { return t('player.layers.net.international'); }, get nwn() { return t('player.layers.net.national'); },
  get rwn() { return t('player.layers.net.regional'); }, get lwn() { return t('player.layers.net.local'); },
  get icn() { return t('player.layers.net.international'); }, get ncn() { return t('player.layers.net.national'); },
  get rcn() { return t('player.layers.net.regional'); }, get lcn() { return t('player.layers.net.local'); },
};
export const routeFamily = (r) => ROUTE_FAMILY[r && r.route] || 'hike';
// is a route shown with these chips ({mtb})? Hiking routes always are
export const routeOn = (r, chips) => routeFamily(r) === 'hike' || !!(chips && chips.mtb);
export const routePlate = (r) => { const S = ROUTE_STYLE[routeFamily(r)]; return BIG_NET.has(r.network) ? S.plate : S.local; };

// the route bands (world transform). hl = the highlighted route index (-1:
// none): the others dim.
export function paintRouteBands(g, routes, rect, k, chips, hl = -1) {
  if (!routes || !routes.length) return;
  g.setLineDash([]); g.lineCap = 'round'; g.lineJoin = 'round';
  for (const dim of [true, false]) {
    for (const fam of Object.keys(ROUTE_STYLE)) {
      g.beginPath();
      let any = false;
      routes.forEach((r, i) => {
        if (routeFamily(r) !== fam || !BIG_NET.has(r.network) || !routeOn(r, chips) || !hit(r.bbox, rect)) return;
        if ((hl >= 0 && i !== hl) !== dim) return;
        for (const s of r.stages) if (hit(s.bbox, rect)) { traceF(g, s.pts, k * 1.2); any = true; }
      });
      if (!any) continue;
      g.globalAlpha = dim && hl >= 0 ? 0.35 : 1;
      g.strokeStyle = ROUTE_STYLE[fam].band; g.lineWidth = 9 * k; g.stroke();
    }
  }
  g.globalAlpha = 1; g.lineCap = 'butt';
}

// swissTLM3D structures on paths (pathinfo.structs {k, x0, z0, x1, z1}),
// ≤ 0.5 m/px: bridge = two side lines, steps = rungs, ford = blue dashes,
// underpass / tunnel / gallery = dashed dark casing
export function paintStructs(g, structs, rect, k) {
  if (!structs || !structs.length) return;
  g.setLineDash([]); g.lineCap = 'butt';
  const groups = { side: [], rung: [], ford: [], cover: [] };
  for (const s of structs) {
    const x0 = Math.min(s.x0, s.x1), x1 = Math.max(s.x0, s.x1), z0 = Math.min(s.z0, s.z1), z1 = Math.max(s.z0, s.z1);
    if (x1 < rect[0] || x0 > rect[2] || z1 < rect[1] || z0 > rect[3]) continue;
    (s.k === 'bridge' ? groups.side : s.k === 'steps' ? groups.rung : s.k === 'ford' ? groups.ford : groups.cover).push(s);
  }
  const off = 3.4 * k;
  const seg = (s) => { const dx = s.x1 - s.x0, dz = s.z1 - s.z0, L = Math.hypot(dx, dz) || 1; return [dx / L, dz / L, L]; };
  if (groups.side.length) {
    g.beginPath();
    for (const s of groups.side) {
      const [ux, uz] = seg(s);
      for (const sd of [-1, 1]) { g.moveTo(s.x0 - uz * off * sd, s.z0 + ux * off * sd); g.lineTo(s.x1 - uz * off * sd, s.z1 + ux * off * sd); }
    }
    g.strokeStyle = 'rgba(20,20,24,0.95)'; g.lineWidth = 1.4 * k; g.stroke();
  }
  if (groups.rung.length) {
    g.beginPath();
    for (const s of groups.rung) {
      const [ux, uz, L] = seg(s);
      for (let d = 0; d <= L; d += 2.6 * k) {
        const x = s.x0 + ux * d, z = s.z0 + uz * d;
        g.moveTo(x - uz * off * 0.8, z + ux * off * 0.8); g.lineTo(x + uz * off * 0.8, z - ux * off * 0.8);
      }
    }
    g.strokeStyle = 'rgba(20,20,24,0.9)'; g.lineWidth = 1.1 * k; g.stroke();
  }
  if (groups.ford.length) {
    g.beginPath();
    for (const s of groups.ford) { g.moveTo(s.x0, s.z0); g.lineTo(s.x1, s.z1); }
    g.strokeStyle = 'rgba(56,189,248,0.95)'; g.lineWidth = 3 * k; g.setLineDash([2 * k, 2 * k]); g.stroke();
  }
  if (groups.cover.length) {
    g.beginPath();
    for (const s of groups.cover) {
      const [ux, uz] = seg(s);
      for (const sd of [-1, 1]) { g.moveTo(s.x0 - uz * off * sd, s.z0 + ux * off * sd); g.lineTo(s.x1 - uz * off * sd, s.z1 + ux * off * sd); }
    }
    g.strokeStyle = 'rgba(40,40,46,0.9)'; g.lineWidth = 1.3 * k; g.setLineDash([3 * k, 2 * k]); g.stroke();
  }
  g.setLineDash([]);
}

// amenities (meta.amenities pts {k, x, z, y, name, ele, t}): kind →
// [human label (en reference — players see AMENITY_TEXT via amenityLabel),
// glyph, label-rule kind (its zoom gate + priority), accent]
export const AMENITY = {
  saddle: ['Saddle', '⌒', 'saddle', '#e5e7eb'],
  cross: ['Cross', '✝', 'cross', '#f5f5f4'],
  drinking_water: ['Drinking water', '🚰', 'amenity', '#38bdf8'],
  spring: ['Spring', '💧', 'amenity', '#38bdf8'],
  toilets: ['Toilets', '🚻', 'amenity', '#a78bfa'],
  firepit: ['Fire pit', '🔥', 'amenity', '#fb923c'],
  bbq: ['Barbecue', '🔥', 'amenity', '#fb923c'],
  picnic_table: ['Picnic table', '🧺', 'amenity', '#a3e635'],
  shelter: ['Shelter', '🛖', 'amenity', '#fbbf24'],
  bench: ['Bench', '🪑', 'amenity_minor', '#d6b38a'],
  waste_basket: ['Waste basket', '🗑', 'amenity_minor', '#9ca3af'],
  board: ['Information board', '🪧', 'amenity_minor', '#fde047'],
  map: ['Map board', '🗺', 'amenity_minor', '#fde047'],
  route_marker: ['Route marker', '🪧', 'amenity_minor', '#fde047'],
  historic: ['Historic site', '🏛', 'amenity_minor', '#e0e7ff'],
  artwork: ['Artwork', '🎨', 'amenity_minor', '#f0abfc'],
  charging_station: ['Charging station', '🔌', 'amenity_minor', '#4ade80'],
  parking: ['Parking', '🅿', 'amenity_minor', '#60a5fa'],
  bicycle_parking: ['Bicycle parking', '🚲', 'amenity_minor', '#60a5fa'],
  playground: ['Playground', '🛝', 'amenity_minor', '#f472b6'],
  survey_point: ['Survey point', '△', 'amenity_minor', '#e5e7eb'],
  bus_stop: ['Bus stop', '🚏', 'amenity', '#facc15'],
};
// the player-facing amenity label per kind, resolved at call time
const AMENITY_TEXT = {
  saddle: () => t('player.layers.amenity.saddle'),
  cross: () => t('player.layers.amenity.cross'),
  drinking_water: () => t('player.layers.amenity.drinking_water'),
  spring: () => t('player.layers.amenity.spring'),
  toilets: () => t('player.layers.amenity.toilets'),
  firepit: () => t('player.layers.amenity.firepit'),
  bbq: () => t('player.layers.amenity.bbq'),
  picnic_table: () => t('player.layers.amenity.picnic_table'),
  shelter: () => t('player.layers.amenity.shelter'),
  bench: () => t('player.layers.amenity.bench'),
  waste_basket: () => t('player.layers.amenity.waste_basket'),
  board: () => t('player.layers.amenity.board'),
  map: () => t('player.layers.amenity.map'),
  route_marker: () => t('player.layers.amenity.route_marker'),
  historic: () => t('player.layers.amenity.historic'),
  artwork: () => t('player.layers.amenity.artwork'),
  charging_station: () => t('player.layers.amenity.charging_station'),
  parking: () => t('player.layers.amenity.parking'),
  bicycle_parking: () => t('player.layers.amenity.bicycle_parking'),
  playground: () => t('player.layers.amenity.playground'),
  survey_point: () => t('player.layers.amenity.survey_point'),
  bus_stop: () => t('player.layers.amenity.bus_stop'),
};
// DETAIL-1 point barriers (amenities.json `street`): drawn in 3D, pickable,
// but OFF the map amenity layer and never an amenity map hit — no AMENITY row.
// Getters: the label is resolved in the current language at each read.
export const STREET_OFFMAP = {
  get gate() { return t('player.layers.street.gate'); },
  get swing_gate() { return t('player.layers.street.swing_gate'); },
  get kissing_gate() { return t('player.layers.street.kissing_gate'); },
  get stile() { return t('player.layers.street.stile'); },
  get lift_gate() { return t('player.layers.street.lift_gate'); },
  get bollard() { return t('player.layers.street.bollard'); },
  get barrier() { return t('player.layers.street.barrier'); },
};
const amenityOf = (p) => AMENITY[p.k] || null;
// the human label of an amenity point (summit cross, milestone …)
export function amenityLabel(p) {
  const A = amenityOf(p), tags = p.t || {};
  if (p.k === 'cross' && tags.summit_cross) return t('player.layers.amenity.summitCross');
  if (p.k === 'historic' && tags.historic) return String(tags.historic).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
  if (!A && STREET_OFFMAP[p.k]) return STREET_OFFMAP[p.k];
  return A ? (AMENITY_TEXT[p.k] ? AMENITY_TEXT[p.k]() : A[0]) : String(p.k).replace(/_/g, ' ');
}
// does the map show this amenity at mppCss?
export const amenityOn = (p, mppCss) => { const A = amenityOf(p); return !!A && mppCss <= LABEL_RULES[A[2]][0]; };
// the nearest amenity shown at mppCss (any zoom when mppCss is null) within r → {p, i, d} | null;
// a kind without an AMENITY row (the street barriers) is never a map amenity
export function amenityNear(list, x, z, r, mppCss = null) {
  let best = null;
  (list || []).forEach((p, i) => {
    if (!amenityOf(p) || Math.abs(p.x - x) > r || Math.abs(p.z - z) > r) return;
    if (mppCss !== null && !amenityOn(p, mppCss)) return;
    const d = Math.hypot(p.x - x, p.z - z);
    if (d <= r && (!best || d < best.d)) best = { p, i, d };
  });
  return best;
}

// arc length index of an interleaved polyline + the point at arc s
function arcOf(P) {
  const n = P.length >> 1, a = new Float64Array(Math.max(1, n));
  for (let i = 1; i < n; i++) a[i] = a[i - 1] + Math.hypot(P[2 * i] - P[2 * i - 2], P[2 * i + 1] - P[2 * i - 1]);
  return a;
}
function arcPoint(P, A, s) {
  const n = P.length >> 1;
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (A[m] <= s) lo = m; else hi = m; }
  const seg = A[hi] - A[lo], u = seg > 1e-9 ? (s - A[lo]) / seg : 0;
  const dx = P[2 * hi] - P[2 * lo], dz = P[2 * hi + 1] - P[2 * lo + 1], d = Math.hypot(dx, dz) || 1;
  return [P[2 * lo] + dx * u, P[2 * lo + 1] + dz * u, dx / d, dz / d];
}
// stamp `arc` + `len` on every entry ({pts}) once per data generation
export function arcStamp(e) {
  if (!e.pts || e.pts.length < 4 || e.arc) return;
  e.arc = arcOf(e.pts); e.len = e.arc[e.arc.length - 1];
}
export function withArc(list) {
  for (const e of list || []) arcStamp(e);
  return list;
}
// the anchor spacing of a repeated-along-line label: px CSS px at mppCss,
// snapped to a power of 2 m (stable while the zoom glides)
export const anchorSpacing = (px, mppCss) => 2 ** Math.round(Math.log2(Math.max(1e-3, px * mppCss)));
// anchors along an entry {pts, arc, len}: n = round(len / sp) ≥ 1 evenly
// spaced at (j + ½)·len / n (a run shorter than sp: its middle, if ≥ minLen)
function alongAnchors(e, sp, minLen, fn) {
  const L = e.len;
  if (!(L >= minLen)) return;
  const n = Math.max(1, Math.round(L / sp));
  for (let j = 0; j < n; j++) {
    const [x, z] = arcPoint(e.pts, e.arc, ((j + 0.5) * L) / n);
    fn(x, z);
  }
}

// ---------------------------------------------------------------- labels
// Candidates in SCREEN px: {text, sx, sy, pri, size (canvas px), color,
// style, angle?, len? (line: the run's px length), sym? (symbol kind),
// ent? (what a tap on the label opens: {kind, name, x, z, poi?})}.
// opts: {view, W, H, px, rect, mppCss, src: {gazetteer, zones, graph, reg
// (registryIndex), guideposts}, combat}
export function collectLabels(o) {
  const { view, W, H, px, rect, mppCss, src } = o;
  const out = [];
  const onScr = (sx, sy, m = 0) => sx >= -m && sy >= -m && sx <= W + m && sy <= H + m;
  const add = (kind, text, x, z, extra = {}) => {
    const R = labelRule(kind);
    if (mppCss > R[0] || !text) return;
    const [sx, sy] = toScreen(view, W, H, x, z);
    if (!onScr(sx, sy, 2)) return;
    out.push({ kind, text, sx, sy, pri: R[1] + (extra.bonus || 0), size: R[2] * px, color: R[3], style: R[4],
      ent: extra.ent || { kind, name: text, x, z } });
  };
  // gazetteer: places, lakes, peaks, rivers, huts, passes, POIs
  const gz = src.gazetteer;
  if (gz && gz.pois) {
    for (const p of gz.pois) {
      if (!p.name || p.kind === 'station') continue;      // stations: the zones below
      if (p.x < rect[0] || p.x > rect[2] || p.z < rect[1] || p.z > rect[3]) continue;
      const summit = p.kind === 'peak' || p.kind === 'farpeak';
      const text = summit && p.ele ? `${p.name} ${Math.round(p.ele)}` : p.name;
      const bonus = summit ? Math.min(30, (p.prom || 0) / 50) : 0;
      add(LABEL_RULES[p.kind] ? p.kind : 'poi', text, p.x, p.z,
        { bonus, ent: { kind: 'poi', name: p.name, x: p.x, z: p.z, poi: p } });
    }
  }
  // stations (zones, not the lite stops)
  const zs = src.zones && src.zones.zones;
  if (zs) for (const zn of zs) if (zn.name && !zn.lite) add('station', zn.name, zn.cx, zn.cz);
  // building names + house numbers (the registry)
  if (src.reg && mppCss <= LABEL_RULES.building[0]) {
    recordsIn(src.reg, rect, (e) => {
      if (e.name) add('building', e.name, e.x, e.z, { ent: { kind: 'building', name: e.name, x: e.x, z: e.z } });
      else if (e.hn) add('address', e.hn, e.x, e.z, { ent: { kind: 'building', name: e.addr, x: e.x, z: e.z } });
    });
  }
  // guidepost (signpost) names + the location plate's elevation
  const gp = src.guideposts && src.guideposts.posts;
  if (gp && mppCss <= LABEL_RULES.guidepost[0]) {
    for (const p of gp) {
      if (!p.name || p.x < rect[0] || p.x > rect[2] || p.z < rect[1] || p.z > rect[3]) continue;
      add('guidepost', Number.isFinite(p.ele) ? `${p.name} ${Math.round(p.ele)}` : p.name, p.x, p.z, { ent: { kind: 'guidepost', name: p.name, x: p.x, z: p.z } });
    }
  }
  // street names: per name, the longest straight run on screen
  if (src.graph && src.graph.ways && mppCss <= LABEL_RULES.street[0]) out.push(...streetLabels(src.graph.ways, o));
  out.push(...trailLabels(o, onScr));
  return out;
}

// the trail ladder's labels (MAP-D). src: trailLines (the map's decorated
// trail entries {pts, arc, len, bbox, mark, chipT, mtb, name, chain}),
// routes (prepRoutes, stages with arc / len), amenities, chips {mtb, amen}
// (hiking always on), hl (the highlighted route index). Shields (≤ 4) every ~250 px
// and grade chips (≤ 2) every ~200 px repeat ALONG the lines at arc anchors
// snapped to a power-of-2 m spacing (deterministic, steady while zooming);
// one per chain + text within 0.6 spacings.
function trailLabels(o, onScr) {
  const { view, W, H, px, rect, mppCss, src } = o;
  const out = [];
  const ch = src.chips || { mtb: false, amen: true };
  const inR = (x, z) => x >= rect[0] && x <= rect[2] && z >= rect[1] && z <= rect[3];
  const near = (list, x, z, d) => list.some((q) => Math.abs(q[0] - x) < d && Math.abs(q[1] - z) < d);
  // route shields
  const RS = LABEL_RULES.shield;
  if (src.routes && mppCss <= RS[0]) {
    const sp = anchorSpacing(250, mppCss);
    src.routes.forEach((r, ri) => {
      if (!r.ref || !routeOn(r, ch) || !hit(r.bbox, rect)) return;
      const plate = routePlate(r), text = String(r.ref).slice(0, 8), seen = [];
      for (const s of r.stages) {
        if (!s.arc || !hit(s.bbox, rect)) continue;
        alongAnchors(s, sp, 0, (x, z) => {
          if (!inR(x, z) || near(seen, x, z, sp * 0.6)) return;
          seen.push([x, z]);
          const [sx, sy] = toScreen(view, W, H, x, z);
          if (!onScr(sx, sy, 2)) return;
          out.push({ kind: 'shield', text, sx, sy, pri: RS[1] + (BIG_NET.has(r.network) ? 2 : 0) + (ri === src.hl ? 4 : 0),
            size: RS[2] * px, color: plate[1], style: 'shield', plate, ent: { kind: 'route', ri, name: r.name || text, x, z } });
        });
      }
    });
  }
  const T = src.trailLines;
  // grade chips: T3 / K3 in the mark colour (always), S2 in the MTB colour (MTB chip)
  const RC = LABEL_RULES.chip;
  if (T && mppCss <= RC[0]) {
    const sp = anchorSpacing(200, mppCss), minLen = 24 * mppCss, seen = new Map();
    for (const e of T) {
      if (!e.arc || !hit(e.bbox, rect)) continue;
      const parts = [];
      if (e.chipT) { const c = CHIP_STYLE[e.mark] || CHIP_STYLE.none; parts.push({ text: e.chipT, bg: c[0], fg: c[1] }); }
      if (ch.mtb && e.mtb) parts.push({ text: e.mtb.label, bg: MTB_RGB[e.mtb.key], fg: '#ffffff' });
      if (!parts.length) continue;
      const text = parts.map((q) => q.text).join(' ');
      const key = `${e.chain ?? e.name ?? ''}|${text}`;
      let list = seen.get(key);
      if (!list) seen.set(key, list = []);
      alongAnchors(e, sp, minLen, (x, z) => {
        if (!inR(x, z) || near(list, x, z, sp * 0.6)) return;
        list.push([x, z]);
        const [sx, sy] = toScreen(view, W, H, x, z);
        if (!onScr(sx, sy, 2)) return;
        out.push({ kind: 'chip', text, parts, sx, sy, pri: RC[1] + (e.mark === 'wbw' || e.mark === 'ferrata' ? 1 : 0),
          size: RC[2] * px, color: parts[0].fg, style: 'chip', ent: { kind: 'trail', name: e.name || '', x, z } });
      });
    }
  }
  // names along the lines: routes, then trails
  if (mppCss <= LABEL_RULES.routename[0] && src.routes) {
    const items = [];
    src.routes.forEach((r, ri) => {
      if (!r.name || !routeOn(r, ch) || !hit(r.bbox, rect)) return;
      for (const s of r.stages) items.push({ name: r.name, pts: s.pts, bbox: s.bbox, ent: { kind: 'route', ri, name: r.name } });
    });
    out.push(...namesAlong(items, 'routename', o));
  }
  if (T && mppCss <= LABEL_RULES.trailname[0]) {
    out.push(...namesAlong(T.filter((e) => e.name && !e.extra), 'trailname', o));
  }
  // amenities: a glyph each (+ the name / the saddle's height)
  const A = src.amenities;
  if (A && ch.amen && mppCss <= LABEL_RULES.saddle[0]) {
    A.forEach((p, i) => {
      const am = AMENITY[p.k];
      if (!am || !inR(p.x, p.z)) return;
      const R = LABEL_RULES[am[2]];
      if (mppCss > R[0]) return;
      const [sx, sy] = toScreen(view, W, H, p.x, p.z);
      if (!onScr(sx, sy, 2)) return;
      const ele = Number.isFinite(p.ele) ? Math.round(p.ele) : null;
      const text = p.name ? (p.k === 'saddle' && ele ? `${p.name} ${ele}` : p.name) : (p.k === 'saddle' && ele ? String(ele) : '');
      out.push({ kind: am[2], text, sx, sy, pri: R[1] + (p.name ? 1 : 0), size: R[2] * px, color: R[3], style: 'icon',
        icon: am[1], accent: am[3], ent: { kind: 'amenity', ai: i, name: p.name || amenityLabel(p), x: p.x, z: p.z } });
    });
  }
  return out;
}

// per name, the longest straight-ish (±20°) run fully on screen of a set of
// polylines {name, pts (xz interleaved), bbox, ent?} → line candidates
function namesAlong(items, kind, o) {
  const { view, W, H, px, rect } = o;
  const R = LABEL_RULES[kind], best = new Map();
  const TOL = Math.cos(20 * Math.PI / 180);
  for (const it of items) {
    const P = it.pts;
    if (!it.name || !P || P.length < 4 || !hit(it.bbox, rect)) continue;
    const n = P.length >> 1;
    let i0 = 0, ux = 0, uz = 0;
    const close = (i1) => {
      if (i1 <= i0) return;
      const [ax, ay] = toScreen(view, W, H, P[2 * i0], P[2 * i0 + 1]), [bx, by] = toScreen(view, W, H, P[2 * i1], P[2 * i1 + 1]);
      if (!(ax >= 0 && ay >= 0 && ax <= W && ay <= H && bx >= 0 && by >= 0 && bx <= W && by <= H)) return;
      const len = Math.hypot(bx - ax, by - ay), cur = best.get(it.name);
      if (!cur || len > cur.len) {
        best.set(it.name, { ax, ay, bx, by, len, x: (P[2 * i0] + P[2 * i1]) / 2, z: (P[2 * i0 + 1] + P[2 * i1 + 1]) / 2, ent: it.ent });
      }
    };
    for (let i = 1; i < n; i++) {
      const dx = P[2 * i] - P[2 * i - 2], dz = P[2 * i + 1] - P[2 * i - 1], L = Math.hypot(dx, dz);
      if (L < 1e-6) continue;
      if (i - 1 === i0) { ux = dx / L; uz = dz / L; continue; }
      if ((dx * ux + dz * uz) / L < TOL) { close(i - 1); i0 = i - 1; ux = dx / L; uz = dz / L; }
    }
    close(n - 1);
  }
  const out = [];
  for (const [name, s] of best) {
    let ang = Math.atan2(s.by - s.ay, s.bx - s.ax);
    if (ang > Math.PI / 2) ang -= Math.PI; else if (ang < -Math.PI / 2) ang += Math.PI;
    out.push({ kind, text: name, sx: (s.ax + s.bx) / 2, sy: (s.ay + s.by) / 2, pri: R[1], size: R[2] * px,
      color: R[3], style: 'line', angle: ang, len: s.len,
      ent: s.ent ? { ...s.ent, x: s.x, z: s.z } : { kind: 'trail', name, x: s.x, z: s.z } });
  }
  return out;
}

// the longest straight-ish run (segment directions within ~20° of its first)
// of every named road, per name, on screen → line candidates
function streetLabels(ways, o) {
  const { view, W, H, px, rect } = o;
  const R = LABEL_RULES.street, best = new Map();
  const TOL = Math.cos(20 * Math.PI / 180);
  for (const w of ways) {
    if (!w.name || w.kind !== 'road' || w.pts.length < 2 || !hit(bboxOf(w, w.pts, 2), rect)) continue;
    const P = w.pts;
    let i0 = 0, ux = 0, uz = 0;
    const close = (i1) => {
      if (i1 <= i0) return;
      const a = P[i0], b = P[i1];
      const [ax, ay] = toScreen(view, W, H, a[0], a[2]), [bx, by] = toScreen(view, W, H, b[0], b[2]);
      if (!(ax >= 0 && ay >= 0 && ax <= W && ay <= H && bx >= 0 && by >= 0 && bx <= W && by <= H)) return;
      const len = Math.hypot(bx - ax, by - ay), cur = best.get(w.name);
      if (!cur || len > cur.len) best.set(w.name, { ax, ay, bx, by, len, x: (a[0] + b[0]) / 2, z: (a[2] + b[2]) / 2 });
    };
    for (let i = 1; i < P.length; i++) {
      const dx = P[i][0] - P[i - 1][0], dz = P[i][2] - P[i - 1][2], L = Math.hypot(dx, dz);
      if (L < 1e-6) continue;
      if (i - 1 === i0) { ux = dx / L; uz = dz / L; continue; }
      if ((dx * ux + dz * uz) / L < TOL) { close(i - 1); i0 = i - 1; ux = dx / L; uz = dz / L; }
    }
    close(P.length - 1);
  }
  const out = [];
  for (const [name, s] of best) {
    let ang = Math.atan2(s.by - s.ay, s.bx - s.ax);
    if (ang > Math.PI / 2) ang -= Math.PI; else if (ang < -Math.PI / 2) ang += Math.PI;
    out.push({ kind: 'street', text: name, sx: (s.ax + s.bx) / 2, sy: (s.ay + s.by) / 2, pri: R[1], size: R[2] * px,
      color: R[3], style: 'line', angle: ang, len: s.len, ent: { kind: 'street', name, x: s.x, z: s.z } });
  }
  return out;
}

// deterministic order: priority, then text, then position
const byPri = (a, b) => b.pri - a.pri || (a.text < b.text ? -1 : a.text > b.text ? 1 : 0) || a.sx - b.sx || a.sy - b.sy;

// Greedy placement in priority order against an occupancy grid of `cell`
// px. measure(text, size) → width px. A dot/peak label tries right, left,
// above, below its symbol; the others sit centred (a line along its run, and
// only when it fits the run). → [{c, box:[x0,y0,x1,y1], x, y, align}]
export function placeLabels(cands, W, H, { cell = 8, measure, pad = 2 } = {}) {
  const gw = Math.max(1, Math.ceil(W / cell)), gh = Math.max(1, Math.ceil(H / cell));
  const occ = new Uint8Array(gw * gh);
  const cells = (b) => [Math.floor(b[0] / cell), Math.floor(b[1] / cell), Math.floor(b[2] / cell), Math.floor(b[3] / cell)];
  const free = (b) => {
    if (b[0] < 0 || b[1] < 0 || b[2] > W || b[3] > H) return false;
    const [c0, r0, c1, r1] = cells(b);
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) if (occ[r * gw + c]) return false;
    return true;
  };
  const take = (b) => {
    const [c0, r0, c1, r1] = cells(b);
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) occ[r * gw + c] = 1;
  };
  const out = [];
  for (const c of [...cands].sort(byPri)) {
    const tw = c.text ? measure(c.text, c.size) : 0, th = c.size * 1.15;
    const sym = c.style === 'dot' || c.style === 'peak' ? c.size * 0.42 : c.style === 'icon' ? c.size * 0.68 : 0;
    let tries;
    if (c.style === 'shield' || c.style === 'chip') {
      const hw = plateWidth(c, measure) / 2 + pad, hh = c.size * 0.75 + pad;
      tries = [{ box: [c.sx - hw, c.sy - hh, c.sx + hw, c.sy + hh], x: c.sx, y: c.sy, align: 'center' }];
    } else if (c.style === 'icon' && !c.text) {
      tries = [{ box: [c.sx - sym - pad, c.sy - sym - pad, c.sx + sym + pad, c.sy + sym + pad], x: c.sx, y: c.sy, align: 'center' }];
    } else if (c.style === 'line') {
      if (!(c.len >= tw + 8 * (c.size / 11))) continue;
      const ca = Math.abs(Math.cos(c.angle)), sa = Math.abs(Math.sin(c.angle));
      const hw = (tw * ca + th * sa) / 2 + pad, hh = (tw * sa + th * ca) / 2 + pad;
      tries = [{ box: [c.sx - hw, c.sy - hh, c.sx + hw, c.sy + hh], x: c.sx, y: c.sy, align: 'center' }];
    } else if (sym) {
      const g = sym + 3;
      tries = [
        { box: [c.sx - sym - pad, c.sy - th / 2 - pad, c.sx + g + tw + pad, c.sy + th / 2 + pad], x: c.sx + g, y: c.sy, align: 'left' },
        { box: [c.sx - g - tw - pad, c.sy - th / 2 - pad, c.sx + sym + pad, c.sy + th / 2 + pad], x: c.sx - g, y: c.sy, align: 'right' },
        { box: [c.sx - tw / 2 - pad, c.sy - sym - th - 2 - pad, c.sx + tw / 2 + pad, c.sy + sym + pad], x: c.sx, y: c.sy - sym - th / 2 - 2, align: 'center' },
        { box: [c.sx - tw / 2 - pad, c.sy - sym - pad, c.sx + tw / 2 + pad, c.sy + sym + th + 2 + pad], x: c.sx, y: c.sy + sym + th / 2 + 2, align: 'center' },
      ];
    } else {
      tries = [{ box: [c.sx - tw / 2 - pad, c.sy - th / 2 - pad, c.sx + tw / 2 + pad, c.sy + th / 2 + pad], x: c.sx, y: c.sy, align: 'center' }];
    }
    const t = tries.find((q) => free(q.box));
    if (!t) continue;
    take(t.box);
    out.push({ c, ...t });
  }
  return out;
}

const boxOverlap = (a, b) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
export { boxOverlap };

// a shield / chip's width: its text (each chip part) + side padding
function plateWidth(c, measure) {
  const padX = c.size * 0.45;
  if (c.style === 'chip' && c.parts && c.parts.length) return c.parts.reduce((a, q) => a + measure(q.text, c.size) + 2 * padX, 0);
  return measure(c.text, c.size) + 2 * padX;
}
function rrect(g, x, y, w, h, r) {
  g.beginPath();
  if (g.roundRect) g.roundRect(x, y, w, h, r); else g.rect(x, y, w, h);
}

// draw the placed labels (screen space, identity transform); px = canvas px per CSS px
export function drawLabels(g, placed, px) {
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  const font = (c) => `700 ${c.size}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
  const measure = (t) => g.measureText(t).width;
  for (const p of placed) {
    const c = p.c;
    // route plates + grade chips: filled boxes, no halo text
    if (c.style === 'shield' || c.style === 'chip') {
      g.font = font(c);
      const W = plateWidth(c, measure), h = c.size * 1.5, x0 = c.sx - W / 2, y0 = c.sy - h / 2, padX = c.size * 0.45;
      g.textAlign = 'center';
      if (c.style === 'shield') {
        const [bg, fg, bd] = c.plate || ['#2F8F3F', '#ffffff', '#ffffff'];
        rrect(g, x0, y0, W, h, 2.5 * px);
        g.fillStyle = bg; g.fill();
        g.lineWidth = 1.3 * px; g.strokeStyle = bd; g.stroke();
        rrect(g, x0 - 1.2 * px, y0 - 1.2 * px, W + 2.4 * px, h + 2.4 * px, 3.4 * px);
        g.lineWidth = 1 * px; g.strokeStyle = 'rgba(8,10,16,.7)'; g.stroke();
        g.fillStyle = fg; g.fillText(c.text, c.sx, c.sy + 0.5 * px);
      } else {
        rrect(g, x0 - 1 * px, y0 - 1 * px, W + 2 * px, h + 2 * px, 3.5 * px);
        g.fillStyle = 'rgba(8,10,16,.75)'; g.fill();
        let x = x0;
        for (const q of c.parts || [{ text: c.text, bg: '#e5e7eb', fg: '#111' }]) {
          const w = measure(q.text) + 2 * padX;
          rrect(g, x, y0, w, h, 2.5 * px);
          g.fillStyle = q.bg; g.fill();
          if (q.bg === '#F4F4F4' || q.bg === '#ffffff') { g.lineWidth = 1 * px; g.strokeStyle = '#555'; g.stroke(); }
          g.fillStyle = q.fg; g.fillText(q.text, x + w / 2, c.sy + 0.5 * px);
          x += w;
        }
      }
      continue;
    }
    // symbol
    if (c.style === 'icon') {
      const s = c.size * 0.68;
      rrect(g, c.sx - s, c.sy - s, 2 * s, 2 * s, 3 * px);
      g.fillStyle = 'rgba(14,18,26,.88)'; g.fill();
      g.lineWidth = 1.2 * px; g.strokeStyle = c.accent || '#e5e7eb'; g.stroke();
      g.font = `${Math.round(c.size * 1.02)}px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif`;
      g.textAlign = 'center'; g.fillStyle = c.accent || '#fff';
      g.fillText(c.icon || '•', c.sx, c.sy + 0.5 * px);
      if (!c.text) continue;
    } else if (c.style === 'peak') {
      const s = c.size * 0.42;
      g.beginPath(); g.moveTo(c.sx, c.sy - s); g.lineTo(c.sx + s * 0.95, c.sy + s * 0.75); g.lineTo(c.sx - s * 0.95, c.sy + s * 0.75); g.closePath();
      g.fillStyle = c.kind === 'farpeak' ? '#8a6a4a' : '#6b3f1d'; g.fill();
      g.lineWidth = 1.2 * px; g.strokeStyle = '#fff'; g.stroke();
    } else if (c.style === 'dot') {
      const s = c.size * 0.32;
      g.beginPath(); g.arc(c.sx, c.sy, s, 0, Math.PI * 2);
      g.fillStyle = SYM[c.kind] || '#e5e7eb'; g.fill();
      g.lineWidth = 1.1 * px; g.strokeStyle = 'rgba(15,15,20,.9)'; g.stroke();
    }
    const italic = c.style === 'water' ? 'italic ' : '';
    const wt = c.style === 'place' ? 700 : c.style === 'num' ? 500 : 600;
    g.font = `${italic}${wt} ${c.size}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
    g.textAlign = p.align;
    g.lineWidth = Math.max(2, c.size * 0.26); g.strokeStyle = 'rgba(8,10,16,.82)'; g.fillStyle = c.color;
    if (c.style === 'line') {
      g.save(); g.translate(p.x, p.y); g.rotate(c.angle);
      g.strokeText(c.text, 0, 0); g.fillText(c.text, 0, 0);
      g.restore();
    } else {
      g.strokeText(c.text, p.x, p.y); g.fillText(c.text, p.x, p.y);
    }
  }
  g.textAlign = 'left';
}

// ----------------------------------------------------------------- hover
// the desktop tooltip: the nearest named thing within r m of (x, z) — cheap
// (gazetteer grid, registry grid, zones, guideposts; not resolveHits).
// → {icon, name} | null
export function hoverPick(src, x, z, r) {
  let best = null, bd = r;
  const take = (d, icon, name) => { if (name && d <= bd) { bd = d; best = { icon, name }; } };
  const gz = src.gazetteer;
  if (gz && gz.within) {
    const p = gz.within(x, z, r)[0];
    if (p) take(p.dist, LABEL_ICON[p.kind] || '📍', p.kind === 'peak' && p.ele ? `${p.name} · ${Math.round(p.ele)} m` : p.name);
  }
  if (src.reg) {
    const n = recordsNear(src.reg, x, z, Math.min(r, 40));
    if (n) take(n.d * 0.9, '🏠', n.e.name ? (n.e.addr ? `${n.e.name} · ${n.e.addr}` : n.e.name) : n.e.addr);
  }
  const zs = src.zones && src.zones.zones;
  if (zs) for (const zn of zs) take(Math.max(0, Math.hypot(zn.cx - x, zn.cz - z) - (zn.r || 0)), '🚉', zn.name);
  const gp = src.guideposts && src.guideposts.posts;
  if (gp && r <= 60) for (const p of gp) if (p.name) take(Math.hypot(p.x - x, p.z - z), '🪧', p.name);
  return best;
}
